import { Boxes, Building2, Cpu, FolderKanban, MessagesSquare, Play, UserRound } from 'lucide-react';
import type { ElementType } from 'react';

import type { AdminLookupKind, AdminLookupResult } from '@/api/adminClient';
import { groupLookupResults } from '@/lib/adminSearch';
import { cn } from '@/lib/utils';

const KIND_ICONS: Record<AdminLookupKind, ElementType> = {
  run: Play,
  session: MessagesSquare,
  project: FolderKanban,
  microvm: Cpu,
  organization: Building2,
  person: UserRound,
};

/** Search hits grouped by kind; `highlighted` is the flat keyboard index. */
export function LookupResults({
  results,
  highlighted,
  onSelect,
  onHighlight,
  listId,
}: {
  results: AdminLookupResult[];
  highlighted: number;
  onSelect: (result: AdminLookupResult) => void;
  onHighlight?: (index: number) => void;
  listId: string;
}) {
  return (
    <div id={listId} role="listbox" aria-label="Search results" className="flex flex-col gap-3">
      {groupLookupResults(results).map((group) => (
        <div key={group.kind} role="group" aria-label={group.label}>
          <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">{group.label}</p>
          {group.items.map(({ result, index }) => {
            const Icon = KIND_ICONS[result.kind] ?? Boxes;
            const active = index === highlighted;
            return (
              <button
                key={`${result.kind}:${result.id}`}
                id={`${listId}-${index}`}
                type="button"
                role="option"
                aria-selected={active}
                tabIndex={-1}
                onMouseMove={() => onHighlight?.(index)}
                onClick={() => onSelect(result)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                  active ? 'bg-secondary text-foreground' : 'text-foreground hover:bg-secondary/60',
                )}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{result.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{result.subtitle}</span>
                </span>
                <code className="hidden max-w-[11rem] shrink-0 truncate pt-0.5 font-mono text-[11px] text-muted-foreground sm:block">{result.id}</code>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
